import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Section, FadeIn } from "@/components/Layout";
import { BUSINESS, IMAGES, TRUST_POINTS, SERVICES, CARE_STEPS, FAQS, GALLERY } from "@/data/site";

const allImages = Object.entries(GALLERY).flatMap(([cat, imgs]) => imgs.map((item) => ({ ...item, cat })));

const Home = () => {
  const navigate = useNavigate();
  const images = allImages;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section id="home" className="relative overflow-hidden scroll-mt-24">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-40 -left-32 w-96 h-96 rounded-full bg-secondary/20 blur-3xl" />
        <Section className="relative py-16 sm:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground leading-[1.05]">
              Personal, home-style pet care for dogs and cats. When You Can't Be There.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
              Private boarding for cats and small dogs, plus dependable in-home pet visits for dogs and cats in {BUSINESS.serviceArea}. Medium to large dogs are cared for by our in-home service only.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button onClick={() => scrollTo("contact")} size="lg" data-testid="hero-contact-btn" className="rounded-full h-14 px-8 text-base transition-transform duration-300 hover:-translate-y-0.5">
                Contact Us
              </Button>
              <Button onClick={() => scrollTo("services")} size="lg" variant="secondary" data-testid="hero-services-btn" className="rounded-full h-14 px-8 text-base transition-transform duration-300 hover:-translate-y-0.5">
                View Services and Prices
              </Button>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
            <div className="rounded-[2rem] overflow-hidden soft-shadow-lg aspect-[4/5] sm:aspect-[5/5]">
              <img src={IMAGES.heroDog} alt="Chocolate doodle relaxing comfortably at home" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-8 w-36 sm:w-44 rounded-[1.5rem] overflow-hidden border-4 border-[#FDFBF7] soft-shadow-lg aspect-square">
              <img src={IMAGES.heroCat} alt="Cat resting comfortably" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -top-4 -right-2 sm:right-4 bg-card rounded-2xl px-5 py-3 soft-shadow flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center"><Icons.PawPrint className="w-5 h-5 text-primary" /></span>
              <div className="leading-tight">
                <p className="text-sm font-bold text-foreground">Dogs & Cats</p>
                <p className="text-xs text-muted-foreground">welcome here</p>
              </div>
            </div>
          </motion.div>
        </Section>
      </section>

      {/* INTRO / ABOUT */}
      <section id="about" className="scroll-mt-24">
        <Section className="py-16 sm:py-20">
          <FadeIn className="max-w-3xl mx-auto text-center">
            <span className="text-sm font-medium tracking-wide text-primary uppercase">About Cozy Pet Care</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mt-3">A comfortable home, not a crowded facility</h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Cozy Pet Care is a local, family-operated pet-care service focused on individual attention, dependable
              communication and following each pet owner's instructions. Every pet in our care receives personal attention
              in a warm, home-style environment — never treated like one of many animals in a busy commercial kennel. We
              keep to your pet's routine, follow your instructions, and give them the calm, familiar comfort they'd have with you.
            </p>
            <div className="mt-8 text-left max-w-2xl mx-auto">
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground text-center mb-5">How Cozy Pet Care Began</h3>
              <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
                <p>Cozy Pet Care began naturally, simply by helping friends who needed someone they could trust to care for their pets. At first, we volunteered to help while friends were away, whether that meant welcoming their pets into our home or stopping by to feed them, spend time with them, and make sure they were comfortable.</p>
                <p>As word spread, more friends, neighbors, and acquaintances began reaching out. What started as an occasional favor quickly grew through personal recommendations from people who appreciated the care and attention their pets received.</p>
                <p>Caring for animals has always been something our entire family truly enjoys. Each family member takes part, and everyone brings their own patience, affection, and sense of responsibility. Because of that shared love for animals, Cozy Pet Care naturally became a family business.</p>
                <p>We understand that pets are not just animals—they are part of the family. Our goal is to provide a safe, comfortable, and loving environment where every pet feels welcomed and cared for. Whether a pet is staying with us or receiving care in their own home, we treat them with the same kindness and attention we would want for our own pets.</p>
                <p>Cozy Pet Care was built through trust, word of mouth, and a genuine love of caring for animals. We are grateful that something we started simply to help friends has grown into an opportunity for our family to care for even more pets in our community.</p>
              </div>
            </div>
          </FadeIn>
        </Section>
      </section>

      {/* TRUST */}
      <div className="bg-accent/40 border-y border-border">
        <Section className="py-16 sm:py-20">
          <FadeIn className="text-center mb-12">
            <span className="text-sm font-medium tracking-wide text-primary uppercase">Why families trust us</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mt-3">Dependable care, every day</h2>
          </FadeIn>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TRUST_POINTS.map((p, i) => {
              const Icon = Icons[p.icon] || Icons.Check;
              return (
                <FadeIn key={p.text} delay={i * 0.04}>
                  <div data-testid="trust-point" className="flex items-center gap-4 bg-card rounded-2xl p-5 soft-shadow h-full transition-transform duration-300 hover:-translate-y-1">
                    <span className="w-12 h-12 shrink-0 rounded-full bg-primary/12 flex items-center justify-center"><Icon className="w-6 h-6 text-primary" /></span>
                    <span className="text-base font-medium text-foreground">{p.text}</span>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </Section>
      </div>

      {/* SERVICES */}
      <section id="services" className="scroll-mt-24">
        <Section className="py-16 sm:py-24">
          <FadeIn className="text-center mb-12">
            <span className="text-sm font-medium tracking-wide text-primary uppercase">Services & Pricing</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mt-3">Our services & clear pricing</h2>
            <p className="mt-4 text-lg text-muted-foreground">Charged per day or per visit.</p>
          </FadeIn>
          <div className="grid gap-8 md:grid-cols-3">
            {SERVICES.map((s, i) => (
              <FadeIn key={s.id} delay={i * 0.08}>
                <div data-testid={`service-card-${s.id}`} className="bg-card rounded-3xl overflow-hidden soft-shadow h-full flex flex-col transition-transform duration-300 hover:-translate-y-1.5">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-heading text-2xl font-semibold text-foreground">{s.title}</h3>
                    <p className="mt-1 text-primary font-bold text-lg">${s.price} <span className="text-muted-foreground font-normal text-base">{s.unit}</span></p>
                    {s.note && <p className="mt-1 text-xs text-muted-foreground italic">{s.note}</p>}
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{s.description}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn>
            <div className="mt-10 rounded-3xl border border-border bg-accent/40 p-6 sm:p-8 flex items-start gap-4">
              <span className="w-11 h-11 shrink-0 rounded-full bg-primary/15 flex items-center justify-center"><Icons.Info className="w-6 h-6 text-primary" /></span>
              <p className="text-sm text-muted-foreground leading-relaxed">
                <span className="text-foreground font-medium">Prices are charged per day or per visit.</span> Final
                availability and total price depend on the number of pets, dates, care requirements, travel distance and any
                special instructions. Grooming and medication administration are available — just ask when you contact us.
              </p>
            </div>
          </FadeIn>
        </Section>
      </section>

      {/* HOW WE CARE */}
      <section id="care" className="scroll-mt-24 bg-accent/30 border-y border-border">
        <Section className="py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-10 items-center mb-14">
            <FadeIn>
              <div className="rounded-3xl overflow-hidden soft-shadow aspect-[4/3]">
                <img src={IMAGES.care} alt="Owner spending time with a dog" className="w-full h-full object-cover" />
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <span className="text-sm font-medium tracking-wide text-primary uppercase">How We Care</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mt-3">Care that follows your lead</h2>
              <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
                We care for every pet according to the instructions provided by the owner. Before service begins, we discuss
                feeding, routines, behavior, medications, emergency contacts and any special needs — so your pet's days stay
                comfortable and predictable.
              </p>
            </FadeIn>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {CARE_STEPS.map((step, i) => {
              const Icon = Icons[step.icon] || Icons.Check;
              return (
                <FadeIn key={step.title} delay={(i % 2) * 0.06}>
                  <div data-testid="care-step" className="bg-card rounded-2xl p-7 soft-shadow h-full transition-transform duration-300 hover:-translate-y-1">
                    <span className="w-12 h-12 rounded-full bg-primary/12 flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-primary" /></span>
                    <h3 className="font-heading text-xl font-semibold text-foreground">{step.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.text}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
          <p className="mt-8 text-center text-sm text-muted-foreground max-w-2xl mx-auto">
            Please note: we do not make medical, veterinary or guaranteed-safety claims. Any concerns are handled by
            contacting you and following the instructions and veterinarian details you provide.
          </p>
        </Section>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="scroll-mt-24">
        <Section className="py-16 sm:py-24">
          <FadeIn className="text-center mb-10">
            <span className="text-sm font-medium tracking-wide text-primary uppercase">Gallery</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mt-3">Some of the Pets We've Had the Pleasure of Caring For</h2>
            <p className="mt-4 text-base text-muted-foreground max-w-2xl mx-auto">Every one of these sweet faces has been cared for by our family.</p>
          </FadeIn>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
            {images.map((img, i) => (
              <FadeIn key={img.src + i} delay={(i % 3) * 0.05} className="mb-6 break-inside-avoid">
                <div data-testid="gallery-image" className="rounded-2xl overflow-hidden soft-shadow group">
                  <img src={img.src} alt={`${img.desc || img.cat} in our care`} width={img.w} height={img.h} loading="lazy" className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
              </FadeIn>
            ))}
          </div>
        </Section>
      </section>

      {/* FAQ */}
      <section id="faq" className="scroll-mt-24 bg-accent/30 border-y border-border">
        <Section className="py-16 sm:py-24">
          <FadeIn className="text-center mb-12">
            <span className="text-sm font-medium tracking-wide text-primary uppercase">FAQ</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mt-3">Frequently Asked Questions</h2>
            {/* subtitle removed */}
          </FadeIn>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-item-${i}`} className="bg-card rounded-2xl border border-border px-6 soft-shadow">
                  <AccordionTrigger className="text-left font-heading text-lg font-medium hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Section>
      </section>

      {/* CONTACT */}
      <section id="contact" className="scroll-mt-24">
        <Section className="py-16 sm:py-24">
          <div className="relative overflow-hidden rounded-[2rem] bg-primary text-primary-foreground px-8 py-14 sm:px-16 sm:py-20 text-center">
            <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold relative">Ready to plan your pet's stay?</h2>
            <p className="mt-4 text-lg text-primary-foreground/85 max-w-2xl mx-auto relative">
              Call, text or email us. We'll talk through your dates and your pet's needs, confirm availability and share your final total.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto relative">
              <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="contact-call" className="flex items-center justify-center gap-3 rounded-2xl bg-white/15 hover:bg-white/25 p-5 transition-colors">
                <Icons.Phone className="w-5 h-5" /><span className="font-medium">Call {BUSINESS.phoneDisplay}</span>
              </a>
              <a href={`sms:${BUSINESS.phoneRaw}`} data-testid="contact-text" className="flex items-center justify-center gap-3 rounded-2xl bg-white/15 hover:bg-white/25 p-5 transition-colors">
                <Icons.MessageSquare className="w-5 h-5" /><span className="font-medium">Text us</span>
              </a>
              <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="flex items-center justify-center gap-3 rounded-2xl bg-white/15 hover:bg-white/25 p-5 transition-colors">
                <Icons.Mail className="w-5 h-5" /><span className="font-medium">Email us</span>
              </a>
            </div>
            <div className="mt-8 max-w-2xl mx-auto relative rounded-2xl bg-white/10 border border-white/20 px-6 py-5 text-left" data-testid="contact-forms-note">
              <p className="text-sm sm:text-base text-primary-foreground/90 leading-relaxed">
                <span className="font-semibold">Ready to book?</span> Once you're confident you'll use our service, please complete and send both the{" "}
                <Link to="/policies/agreement" className="underline underline-offset-2 font-medium hover:text-white" data-testid="contact-agreement-link">Pet Care Agreement</Link>{" "}
                and the{" "}
                <Link to="/policies/emergency" className="underline underline-offset-2 font-medium hover:text-white" data-testid="contact-emergency-link">Emergency Care Authorization</Link>. You'll also find links to both forms at the bottom of this page. Your reservation is confirmed only once Cozy Pet Care has received both completed forms.
              </p>
            </div>
            <p className="mt-6 text-sm text-primary-foreground/70 relative">Serving {BUSINESS.serviceArea}</p>
          </div>
        </Section>
      </section>
    </div>
  );
};

export default Home;
